import { Action, Location } from 'history';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    Admin2FAModal,
    Alert,
    autoinject,
    bind,
    ErrorStore,
    ErrorView,
    ImpersonatingAlert,
    inject,
    LoadGoogleMapScript,
    MainContainer,
    ModalHandle,
    NotificationAlert,
    NotificationToastList,
    PrivateRoute,
    SidebarObfuscator,
} from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Route } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import { AccountStore } from '../Account/AccountStore';
import { InfrastructureView } from '../Infrastructure/InfrastructureView';
import { PodChartsView } from '../PodCharts/PodChartsView';
import { SeqLogsView } from '../SeqLogs/SeqLogsView';
import { SmartVuMetadata } from '../SmartVuMetadata/SmartVuMetadata';
import { AuditStore } from '../Audit/AuditStore';
import { AppStore } from './AppStore';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { UpdateModal } from './UpdateModal';
import {IdleCheck} from "./IdleCheck";

export interface IAppProps extends RouteComponentProps<undefined> {}

// The admin-ops pages that are still served from Internal. Everything else Internal used to host now lives
// on Portal, so any other path falls through to the "Internal Has Moved to Portal" card below — including
// stale deep-link bookmarks like /displays, which are exactly who that message is written for.
//
// Keep this in step with the routes further down: a page added there but missed here renders its content
// underneath the moved-to-Portal card.
const ADMIN_OPS_PATHS = ['/infrastructure', '/seq-logs', '/pod-charts', '/smartvu-metadata'];

function isAdminOpsPath(pathname: string): boolean {
    return ADMIN_OPS_PATHS.some(p => pathname.startsWith(p));
}

@autoinject
@observer
export class App extends React.Component<IAppProps> {
    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.accountStore.loadUserAsync();
            const user = this.accountStore.user;

            if (user) {
                this.appStore.storage.theme = user.preferredTheme as any;
                
                this.accountStore.mergeOldAccountStorageValues();
                
                if (this.appStore.storage.version != user.viewedChangelog) {
                    // Uncomment when we fix the changelog
                    // this.updateModalHandle.open();

                    try {
                        user.viewedChangelog = this.appStore.storage.version;
                        await this.accountStore.setViewedChangelog(this.appStore.storage.version);
                    } catch (e: any) {
                        console.error(e);
                    }
                }

                if (this.accountStore.storage.displaysPreference != 'grid' && this.accountStore.storage.displaysPreference != 'list') {
                    this.accountStore.storage.displaysPreference = 'grid';
                }
            }
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }

        await this.reportPageView(this.props.location);
        this.props.history.listen(this.onHistoryChange);
    }

    public render() {
        if (this.loading) return <div>Loading...</div>;

        const authority = (window as any).initialOidcConfig ? (window as any).initialOidcConfig.authority : '';

        const isMriAdmin = this.accountStore.isInRole('MriAdmin');
        // Non-prod exposes the admin ops pages (Infrastructure / Seq Logs) without login/role so
        // they're always reachable for diagnostics; prod keeps the full MriAdmin-gated PrivateRoute.
        const canSeeAdminOps = isMriAdmin || __NONPROD__;
        // SmartVu Metadata mirrors this app's MriEmployeePolicy, which is wider than MriAdmin.
        const canSeeMetadata = this.accountStore.isInRoles(['MriAdmin', 'MriUser']) || __NONPROD__;

        // An admin-ops path suppresses the moved-to-Portal card, so a user without the role for the
        // page it names would otherwise be shown a completely empty container — no page, no card and
        // no reason. That strands anyone following a stale link, which now includes the roles that
        // could open SmartVu Metadata on Portal before it moved here.
        const path = this.props.location.pathname;
        const canSeeCurrentAdminPage = path.startsWith('/smartvu-metadata') ? canSeeMetadata : canSeeAdminOps;
        const showAdminOpsDenied = isAdminOpsPath(path) && !canSeeCurrentAdminPage;

        return (
            <ErrorView error={this.appStore.error} errorStore={this.errorStore}>
                <div className={this.layoutClassName}>
                    <IdleCheck/>
                    
                    <Header history={this.props.history} onMenuLinkClick={this.toggleSidebar} />

                    <NotificationToastList />

                    <Sidebar
                        closeSidebar={this.closeSidebar}
                        collapseSidebar={this.collapseSidebar}
                        sidebarCollapsed={this.appStore.storage.sidebarCollapsed && !this.appStore.isMedium}
                        sidebarVisible={this.sidebarVisible}
                        match={this.props.match}
                        history={this.props.history}
                        location={this.props.location}
                        staticContext={this.props.staticContext}
                    />

                    <SidebarObfuscator onClick={this.closeSidebar} active={this.sidebarVisible} />

                    <MainContainer mainCointainerRef={this.setMainContainer} onScroll={this.updateScrollPosition}>
                        <span className="text-center">
                            <Alert alertHandle={this.appStore.alertHandle} />
                        </span>
                        <span>
                            <NotificationAlert notificationHandle={this.appStore.notificationHandle} />
                        </span>

                        <ImpersonatingAlert
                            isImpersonating={this.accountStore.user?.isImpersonating ?? false}
                            onClick={this.accountStore.signoutRedirect}
                        />
                       
                        {showAdminOpsDenied && (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                                <div className="card border-0 shadow" style={{ maxWidth: '600px', width: '100%' }}>
                                    <div className="card-body text-center p-5">
                                        <h2 className="card-title mb-3 fw-bold">You don't have access to this page</h2>
                                        <p className="card-text mb-4">
                                            This is an internal operations page and your account does not hold
                                            the role it requires. If you reached it from a bookmark, the page may
                                            have moved or its access may have changed.
                                        </p>
                                        <a href={this.accountStore.portalUrl} className="btn btn-primary btn-lg px-5">
                                            Go to Portal &rarr;
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isAdminOpsPath(this.props.location.pathname) && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: isMriAdmin ? 'auto' : '60vh' }}>
                            <div className="card border-0 shadow" style={{ maxWidth: '600px', width: '100%', marginTop: isMriAdmin ? '2rem' : 0 }}>
                                <div className="card-body text-center p-5">
                                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#127760;</div>
                                    <h2 className="card-title mb-3 fw-bold">Internal Has Moved to Portal</h2>
                                    <p className="card-text mb-4" style={{ fontSize: '1.1rem' }}>
                                        All features previously available here — Displays, Accounts, Tickets,
                                        Production, and more — are now on <strong>Portal</strong>.
                                        Please update your bookmarks.
                                    </p>
                                    <a
                                        href={this.accountStore.portalUrl}
                                        className="btn btn-primary btn-lg px-5"
                                    >
                                        Go to Portal &rarr;
                                    </a>
                                </div>
                            </div>
                        </div>}

                        {canSeeAdminOps &&
                            (__NONPROD__ ? (
                                <Route path="/infrastructure" component={InfrastructureView} />
                            ) : (
                                <PrivateRoute
                                    path="/infrastructure"
                                    user={this.accountStore.user}
                                    roles={['MriAdmin']}
                                    component={InfrastructureView}
                                />
                            ))}

                        {canSeeAdminOps &&
                            (__NONPROD__ ? (
                                <Route path="/seq-logs" component={SeqLogsView} />
                            ) : (
                                <PrivateRoute
                                    path="/seq-logs"
                                    user={this.accountStore.user}
                                    roles={['MriAdmin']}
                                    component={SeqLogsView}
                                />
                            ))}

                        {canSeeAdminOps &&
                            (__NONPROD__ ? (
                                <Route path="/pod-charts" component={PodChartsView} />
                            ) : (
                                <PrivateRoute
                                    path="/pod-charts"
                                    user={this.accountStore.user}
                                    roles={['MriAdmin']}
                                    component={PodChartsView}
                                />
                            ))}

                        {/* MRI employees only - it names internal repositories, branches and pipeline
                            ids. Roles mirror this app's MriEmployeePolicy exactly, so the menu entry,
                            the route and the endpoint cannot disagree. */}
                        {(this.accountStore.isInRoles(['MriAdmin', 'MriUser']) || __NONPROD__) &&
                            (__NONPROD__ ? (
                                <Route path="/smartvu-metadata" exact component={SmartVuMetadata} />
                            ) : (
                                <PrivateRoute
                                    path="/smartvu-metadata"
                                    exact
                                    user={this.accountStore.user}
                                    roles={['MriAdmin', 'MriUser']}
                                    component={SmartVuMetadata}
                                />
                            ))}
                    </MainContainer>

                    <Admin2FAModal
                        location={this.props.location}
                        url={`${authority}/preferences`}
                        requires2FA={
                            !!this.accountStore.user &&
                            !this.accountStore.user.twoFactorEnabled &&
                            !this.accountStore.isTestUser &&
                            this.accountStore.isInRole('MriAdmin')
                        }
                    />
                    <UpdateModal modalHandle={this.updateModalHandle} />
                </div>
            </ErrorView>
        );
    }

    @bind
    @action
    private updateScrollPosition() {
        if (this.appStore.mainContainer) {
            this.appStore.storage.lastMainContainerScrollPositionX = this.appStore.mainContainer.scrollLeft;
            this.appStore.storage.lastMainContainerScrollPositionY = this.appStore.mainContainer.scrollTop;
        }
    }

    @bind
    @action
    private setMainContainer(r: HTMLDivElement | null) {
        this.appStore.mainContainer = r;
    }

    @bind
    @action
    private async toggleSidebar(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.sidebarVisible = !this.sidebarVisible;
    }

    @bind
    @action
    private async closeSidebar(event: React.MouseEvent<LinkContainer | HTMLAnchorElement | HTMLDivElement>) {
        this.sidebarVisible = false;
    }

    @bind
    private onHistoryChange(location: Location, trigger: Action): any {
        this.appStore.showHeader();

        this.reportPageView(location);
    }

    private async reportPageView(location: Location) {
        try {
            const match = /^\/displays\/(?<displaySerialNumber>[A-Z0-9]+)/.exec(location.pathname);
            const displaySerialNumber = match && match.groups ? match.groups.displaySerialNumber : null;

            await this.auditStore.logPageView(location.pathname, displaySerialNumber);
        } catch (e: any) {
            console.error(e, 'Failed to logPageView');
        }
    }

    @bind
    @action
    private collapseSidebar() {
        this.appStore.storage.sidebarCollapsed = !this.appStore.storage.sidebarCollapsed;
    }

    @computed
    private get layoutClassName() {
        return `layout-container ${
            !this.appStore.storage.sidebarCollapsed ? 'layout-sidebar' : 'layout-sidebar-collapsed'
        }`;
    }

    @observable
    private sidebarVisible = false;
    @observable
    private loading = true;
    private updateModalHandle = new ModalHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private auditStore!: AuditStore;
    @inject
    private errorStore!: ErrorStore;
}
